// MACHINERY V2 (Stroje) — Servisné správy: the two row chips + the cost/grid
// contract (M2.5).
//
// Spec: data package §4 + ticket §7 fixes 1 and 3. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`PtStatusChip`,
// `PtSrcChip`); artboards 1000 / 1001 (desktop, both palettes), 1012 (phone).
//
// ── FIX 1 (ticket §7): sourceType 4 = MIDAS, and it no longer says
//    "Maintenance Plan". v1 branches on a TWO-armed ternary
//    (components/SourceTypeBadge.tsx:21-23: `=== requestForService ? rfs :
//    maintenance`), so every non-RFS, non-manual value — including 4 — fell into
//    the maintenance arm. `ServiceReportSourceType` had no `midas` member at all
//    (models/ServiceReportsModel.ts), so the fix is the enum member plus the
//    third arm plus the new `sourceType.midas` key in en + sk.
//
// ── FIX 3 (ticket §7): the cost is formatted from
//    the DTO currency, in the active UI locale (USER-LANG-LEFTOVERS-1; was a
//    hard-coded sk-SK). v1 hard-codes
//    `new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })`
//    (components/ServiceReports.tsx:250 — the only "de-DE" in the whole client,
//    grep-verified), which renders a USD cost as euros. `Money` carries the
//    currency as `id: CurrencyCode` — a NUMERIC ISO-4217 code (840 / 978,
//    common/models/CommonModels.ts:13-16) — and Intl wants the ALPHA code, so
//    the map below is the translation, not a re-derivation.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { CurrencyCode, Money } from "../../../../../common/models/CommonModels";
import {
  ServiceReportSourceType,
  ServiceReportStatusCode,
} from "../../../models/ServiceReportsModel";
import { SR_TONE_VAR, srStatusTone } from "./srStatusTone";
import { getUiLocale } from "../../../../../shared/utils/relativeTime";

/** Desktop column template — artboard 1000 (SR_GRID in the handoff). */
export const SR_GRID = "116px minmax(200px, 1fr) 138px 148px 148px 92px 40px";

const CURRENCY_ALPHA: Record<number, string> = {
  [CurrencyCode.eur]: "EUR",
  [CurrencyCode.usd]: "USD",
};

/**
 * Formats a bare decimal in a report's currency. EXTRACTED at M2.6 so the SR
 * DETAIL page (fix 3's other half) has ONE currency implementation to import —
 * `formatSrCost` below now delegates to it, and nothing formats money twice.
 *
 * It exists separately from `formatSrCost` because the RFS `actualPartsCost` is
 * a bare `decimal?` with NO currency of its own (data package §8) and because
 * ZERO is a real value for it — v1 renders "€0.00" there
 * (srDetail/SrCostCard.tsx:99-103), while a zero `Cost` means "not costed yet".
 *
 * The alpha fallback is load-bearing: `Intl.NumberFormat` throws a RangeError on
 * an unrecognised currency string, and a table cell must never be able to take
 * the page down.
 */
export const formatSrAmount = (value: number, currency?: CurrencyCode): string => {
  const alpha = (currency !== undefined && CURRENCY_ALPHA[currency]) || "EUR";
  return new Intl.NumberFormat(getUiLocale(), { style: "currency", currency: alpha }).format(value);
};

/**
 * `null` when there is nothing to show — v1 renders "-" for a null OR ZERO cost
 * and gates the phone line on `> 0`, so zero is deliberately "no cost recorded",
 * not "0,00 €". Callers render the em-dash.
 */
export const formatSrCost = (cost?: Money): string | null => {
  const value = cost?.value;
  if (value === null || value === undefined || value === 0) return null;
  return formatSrAmount(value, cost?.id);
};

/**
 * Status chip — green solved / amber in-progress (artboard 1000).
 *
 * THE GRADE IS NOT DECIDED HERE (M2.16d review item 1). `srStatusTone` owns
 * "solved is green, anything else is amber" for the whole module, so this chip
 * and the Prehľad tab's activity pip cannot colour the SAME report two
 * different ways on two tabs of one profile. `solved` below is read BACK from
 * the tone rather than re-tested, so the label and the colour are one decision.
 */
export const SrStatusChip: React.FC<{ code: ServiceReportStatusCode }> = ({ code }) => {
  const { translate } = useTranslation("myMachinery");
  const tone = srStatusTone(code);
  const solved = tone === "green";
  const token = SR_TONE_VAR[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 22,
        padding: "0 9px",
        borderRadius: 6,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color: "color-mix(in oklab, var(" + token + ") 78%, var(--ink))",
        background: "color-mix(in oklab, var(" + token + ") 12%, var(--card))",
        border: "1px solid color-mix(in oklab, var(" + token + ") 38%, var(--border))",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 99, background: "var(" + token + ")" }} />
      {translate(
        solved ? "myMachinery:serviceReportStatus.solved" : "myMachinery:serviceReportStatus.inProgress"
      )}
    </span>
  );
};

/** Source chip. Manual (1) and an absent value render nothing — FIX 1 above. */
export const SrSourceChip: React.FC<{
  sourceType?: ServiceReportSourceType;
  requestNumber?: string;
}> = ({ sourceType, requestNumber }) => {
  const { translate } = useTranslation("myMachinery");
  if (!sourceType || sourceType === ServiceReportSourceType.manual) return null;

  const isMidas = sourceType === ServiceReportSourceType.midas;
  const isRfs = sourceType === ServiceReportSourceType.requestForService;
  const label = isMidas
    ? translate("myMachinery:sourceType.midas")
    : isRfs
    ? translate("myMachinery:sourceType.rfs")
    : translate("myMachinery:sourceType.maintenance");

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 8.5,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          padding: "2px 6px",
          borderRadius: 4,
          whiteSpace: "nowrap",
          color: isMidas ? "var(--accent)" : "var(--muted)",
          background: isMidas ? "var(--accent-soft)" : "var(--bg-soft)",
          border: "1px solid " + (isMidas ? "var(--accent-line)" : "var(--border)"),
        }}
      >
        {label}
      </span>
      {/* The RFS number rides with the chip. v1 shows it on the phone card only;
          the chip itself moved to desktop (§5 #4), so its number follows it —
          the `serviceRequestInternalNumber` binding is kept, not dropped. */}
      {isRfs && requestNumber && (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)" }}>
          #{requestNumber}
        </span>
      )}
    </span>
  );
};
